/**
 * PortElementForm — Odoo-style full-screen detail view
 * Tabs: Identificación / Puertos / Ubicación / Hardware
 * SmartButtons: port count, status, rack, location
 */

import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
  MapPinIcon,
  CubeIcon,
  SparklesIcon,
  BoltIcon,
  PencilSquareIcon,
  ArrowsRightLeftIcon,
  TableCellsIcon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import Dialog from './common/Dialog';
import LocationTreeSelect from './common/LocationTreeSelect';
import SwitchPortsTab from './SwitchPortsTab';
import SwitchPsuSection from './SwitchPsuSection';
import ElementIpPanel from './ElementIpPanel';
import DeviceAccessPanel from './DeviceAccessPanel';
import NetworkConnectionsPanel from './NetworkConnectionsPanel';
import PowerConnectionsPanel from './PowerConnectionsPanel';
import PDUOutletMap from './PDUOutletMap';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import { buildLocationMap, locationPath } from '../utils/locationTree';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import ConnectionDiagram from './ConnectionDiagram';

const PORT_PRESETS = [8, 12, 16, 24, 48, 96];

const LABELS = {
  panel: {
    singular: 'Patch Panel',
    plural:   'Patch Panels',
    icon:     '🔌',
    portLabel: 'Número de puertos (RJ45 / fibra)',
    portHint:  'Se crearán N puertos vacíos que podrás parchear desde el visor de puertos.',
  },
  switch: {
    singular: 'Switch de red',
    plural:   'Switches de red',
    icon:     '🔀',
    portLabel: 'Número de puertos',
    portHint:  'Se crearán N puertos vacíos que podrás conectar desde el visor de puertos.',
  },
  pdu: {
    singular: 'PDU',
    plural:   'PDUs',
    icon:     '⚡',
    portLabel: 'Número de enchufes',
    portHint:  'Se crearán N enchufes vacíos que podrás conectar a servidores, switches, etc.',
  },
};


function formatMergeValue(value) {
  if (value === null || value === undefined) return 'Vacio';
  if (typeof value === 'string') return value.trim() || 'Vacio';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Vacio';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function buildDefaultMergeResolutions(fieldPreview) {
  return (fieldPreview || []).reduce((acc, item) => {
    if (item?.resolution === 'conflict' && item?.field) {
      acc[item.field] = 'target';
    }
    return acc;
  }, {});
}

function formatMergeList(values) {
  if (!Array.isArray(values) || values.length === 0) return 'Ninguno';
  return values.join(', ');
}

function buildElementMergeCandidateScore(source, candidate) {
  let score = 0;
  if (source?.serial_number && candidate?.serial_number && source.serial_number === candidate.serial_number) score += 50;
  if (source?.code && candidate?.code && source.code === candidate.code) score += 30;
  if (source?.name && candidate?.name && source.name === candidate.name) score += 15;
  if (source?.manufacturer && candidate?.manufacturer && source.manufacturer === candidate.manufacturer) score += 5;
  if (source?.model && candidate?.model && source.model === candidate.model) score += 5;
  if (source?.rack_id && candidate?.rack_id && String(source.rack_id) === String(candidate.rack_id)) score += 5;
  if (source?.location_id && candidate?.location_id && String(source.location_id) === String(candidate.location_id)) score += 3;
  return score;
}

function PortElementForm({ elementType, item, companyId, initialMode = 'create', onClose, onSave, navItems, navIndex, onNavigate, variant = 'classic' }) {
  const lbl    = LABELS[elementType] || LABELS.panel;
  const isEdit = Boolean(item?.id);
  // Variante «tarjetas» (estándar del CMDB). La clásica no cambia.
  const isCards = variant === 'cards';

  // Modo lectura por defecto en las fichas existentes (estándar del CMDB); el
  // alta y el botón «Editar» del listado piden `initialMode="edit"`.
  const [editing, setEditing] = useState(!isEdit || initialMode === 'edit');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [saving,    setSaving]    = useState(false);
  const [savedOk,   setSavedOk]   = useState(false);
  const [cloning,     setCloning]     = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [cloneName,    setCloneName]    = useState('');
  const cloneNameRef = useRef(null);
  const [apiError,  setApiError]  = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [racks,     setRacks]     = useState([]);
  const [customPorts, setCustomPorts] = useState('');
  const [isDirty,   setIsDirty]   = useState(false);
  // En un alta el código copia al nombre hasta que alguien escriba el código a
  // mano (o lo genere con ✨): así no hay que teclearlo dos veces cuando, como
  // es habitual, son el mismo texto.
  const [codeLocked, setCodeLocked] = useState(false);
  // Último nombre guardado: el título en línea se compara con esto y no con la
  // fila del listado, que puede venir desfasada tras renombrar (igual que en la
  // ficha canónica del rack).
  const [committedName, setCommittedName] = useState(item?.name || '');
  // Puertos del panel/switch, para el bloque de ocupación del Resumen.
  const [ports, setPorts] = useState([]);
  // En tarjetas la primera pestaña es siempre Resumen.
  const [activeTab, setActiveTab] = useState(isCards ? 'general' : 'identificacion');
  const [ipCount,   setIpCount]   = useState(0);
  const [accessCount, setAccessCount] = useState(0);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [addingManufacturer, setAddingManufacturer] = useState(false);
  const [addingModel, setAddingModel] = useState(false);
  const [newManufacturer, setNewManufacturer] = useState('');
  const [newModel, setNewModel] = useState('');
  const [showMergePanel, setShowMergePanel] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState([]);
  const [mergeCandidatesLoading, setMergeCandidatesLoading] = useState(false);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [mergePreview, setMergePreview] = useState(null);
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [mergeResolutions, setMergeResolutions] = useState({});
  const [mergeRelationResolutions, setMergeRelationResolutions] = useState({});
  const [mergeExecuting, setMergeExecuting] = useState(false);
  const [mergeError, setMergeError] = useState('');
  const [mergeResult, setMergeResult] = useState(null);

  const PORT_DEFAULT = elementType === 'pdu' ? 8 : 24;

  const [form, setForm] = useState({
    name: '', code: '', manufacturer: '', model: '',
    serial_number: '', description: '', notes: '',
    location_id: '', rack_id: '', size_u: 1,
    port_count: PORT_DEFAULT, status: 'active',
    // PoE (solo switches)
    poe_capable: false, poe_budget_w: '',
  });

  useEffect(() => {
    // Esc cierra la ficha, pero nunca mientras se está editando o hay cambios
    // sin guardar: antes cerraba también en ese caso y se perdía lo escrito.
    const handler = e => {
      if (e.key !== 'Escape') return;
      if (editing || isDirty) return;
      onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, editing, isDirty]);

  useEffect(() => {
    if (!companyId) return;
    api.getLocations('', companyId).then(r => setLocations(r.data || [])).catch(() => {});
    api.getRacks(null, companyId).then(r => setRacks(r.data || [])).catch(() => {});
  }, [companyId]);

  const loadCatalogs = useCallback(async () => {
    if (!companyId || elementType !== 'switch') {
      setManufacturerOptions([]);
      setModelOptions([]);
      return;
    }
    try {
      const [mfrRes, modelRes] = await Promise.all([
        api.getManufacturers(companyId, { deviceType: 'switch' }).catch(() => ({ data: [] })),
        api.getDeviceModels(companyId, { deviceType: 'switch' }).catch(() => ({ data: [] })),
      ]);
      setManufacturerOptions(Array.isArray(mfrRes?.data) ? mfrRes.data : []);
      setModelOptions(Array.isArray(modelRes?.data) ? modelRes.data : []);
    } catch (_) {}
  }, [companyId, elementType]);

  useEffect(() => { loadCatalogs(); }, [loadCatalogs]);

  useEffect(() => {
    if (item) {
      setForm({
        name:          item.name          || '',
        code:          item.code          || '',
        manufacturer:  item.manufacturer  || '',
        model:         item.model         || '',
        serial_number: item.serial_number || '',
        description:   item.description   || '',
        notes:         item.notes         || '',
        location_id:   item.location_id   ? String(item.location_id) : '',
        rack_id:       item.rack_id       ? String(item.rack_id)     : '',
        size_u:        item.size_u        || 1,
        port_count:    item.port_count    || PORT_DEFAULT,
        status:        item.status        || 'active',
        poe_capable:   !!item.poe_capable,
        poe_budget_w:  item.poe_budget_w != null ? String(item.poe_budget_w) : '',
      });
      setCommittedName(item.name || '');
    } else {
      setCodeLocked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);
  // Puertos del elemento, para el bloque de ocupación del Resumen. Las PDUs
  // usan enchufes (otra API), así que aquí solo van paneles y switches.
  useEffect(() => {
    if (!isEdit || !item?.id || elementType === 'pdu') { setPorts([]); return undefined; }
    let cancelado = false;
    api.getPorts(item.id, companyId)
      .then(r => { if (!cancelado) setPorts(r.data || []); })
      .catch(() => { if (!cancelado) setPorts([]); });
    return () => { cancelado = true; };
  }, [isEdit, item?.id, companyId, elementType]);

  useEffect(() => {
    setShowMergePanel(false);
    setMergeCandidates([]);
    setMergeCandidatesLoading(false);
    setMergeSearch('');
    setMergeTargetId('');
    setMergePreview(null);
    setMergePreviewLoading(false);
    setMergeResolutions({});
    setMergeRelationResolutions({});
    setMergeExecuting(false);
    setMergeError('');
    setMergeResult(null);
  }, [item?.id]);

  const filteredRacks = useMemo(() => {
    if (!form.location_id) return racks;
    return racks.filter(r => String(r.location_id) === String(form.location_id));
  }, [racks, form.location_id]);

  const selectedRack = useMemo(() =>
    racks.find(r => String(r.id) === String(form.rack_id)),
    [racks, form.rack_id]
  );

  const selectedLocation = useMemo(() =>
    locations.find(l => String(l.id) === String(form.location_id)),
    [locations, form.location_id]
  );

  const filteredModelOptions = useMemo(() => {
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    const mid = matchedMfr?.id;
    return (modelOptions || []).filter(m => !mid || m.manufacturer_id === mid || !m.manufacturer_id);
  }, [modelOptions, manufacturerOptions, form.manufacturer]);

  const filteredMergeCandidates = useMemo(() => {
    const normalizedSearch = mergeSearch.trim().toLowerCase();
    return (mergeCandidates || [])
      .filter((candidate) => {
        if (!candidate || String(candidate.id) === String(item?.id)) return false;
        if ((candidate.status || '').toLowerCase() === 'merged') return false;
        if (!normalizedSearch) return true;
        return [
          candidate.name,
          candidate.code,
          candidate.serial_number,
          candidate.manufacturer,
          candidate.model,
          candidate.location_name,
          candidate.rack_name,
        ].some((value) => String(value || '').toLowerCase().includes(normalizedSearch));
      })
      .sort((left, right) => {
        const scoreDiff = buildElementMergeCandidateScore(item, right) - buildElementMergeCandidateScore(item, left);
        if (scoreDiff !== 0) return scoreDiff;
        return String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base' });
      });
  }, [mergeCandidates, mergeSearch, item]);

  const mergeConflictFields = useMemo(() => (
    (mergePreview?.field_preview || []).filter((previewItem) => previewItem.resolution === 'conflict')
  ), [mergePreview]);

  const mergeMovableRelations = useMemo(() => (
    (mergePreview?.relation_preview || []).filter((previewItem) => previewItem.source_count > 0)
  ), [mergePreview]);

  const mergeSwitchPortsRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((previewItem) => previewItem.key === 'owned_switch_ports') || null
  ), [mergePreview]);

  const mergePatchPortsRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((previewItem) => previewItem.key === 'owned_patch_ports') || null
  ), [mergePreview]);

  const mergeIncomingSwitchLinksRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((previewItem) => previewItem.key === 'incoming_switch_links') || null
  ), [mergePreview]);

  const mergeIncomingPatchLinksRelation = useMemo(() => (
    (mergePreview?.relation_preview || []).find((previewItem) => previewItem.key === 'incoming_patch_links') || null
  ), [mergePreview]);

  const mergeUnresolvedBlocks = useMemo(() => (
    (mergePreview?.blocks || []).filter((block) => {
      if (block.key === 'owned_switch_ports') {
        return !mergeRelationResolutions.owned_switch_ports;
      }
      if (block.key === 'owned_patch_ports') {
        return !mergeRelationResolutions.owned_patch_ports;
      }
      if (block.key === 'incoming_switch_links') {
        return !mergeRelationResolutions.incoming_switch_links;
      }
      if (block.key === 'incoming_patch_links') {
        return !mergeRelationResolutions.incoming_patch_links;
      }
      return true;
    })
  ), [mergePreview, mergeRelationResolutions]);

  const canExecuteMerge = Boolean(
    mergePreview && mergeTargetId && mergeUnresolvedBlocks.length === 0
  );

  const selectedMergeCandidate = useMemo(() => (
    mergeCandidates.find((candidate) => String(candidate.id) === String(mergeTargetId)) || null
  ), [mergeCandidates, mergeTargetId]);

  const saveManufacturerToCatalog = async (nameOverride) => {
    if (elementType !== 'switch') return;
    const name = nameOverride || form.manufacturer;
    if (!name || !companyId) return;
    try {
      await api.createManufacturer({ name, device_type: 'switch' }, companyId);
      await loadCatalogs();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar fabricante');
    }
  };

  const saveModelToCatalog = async (modelNameOverride) => {
    if (elementType !== 'switch') return;
    const modelName = modelNameOverride || form.model;
    if (!modelName || !companyId) return;
    const matchedMfr = manufacturerOptions.find(m => (m.name || '').toLowerCase() === (form.manufacturer || '').toLowerCase());
    try {
      await api.createDeviceModel({ name: modelName, device_type: 'switch', manufacturer_id: matchedMfr?.id }, companyId);
      await loadCatalogs();
    } catch (e) {
      setApiError(e.message || 'No se pudo guardar modelo');
    }
  };

  const setField = (name, value) => {
    // El código escrito a mano manda: a partir de ahí el nombre ya no lo pisa.
    if (name === 'code') setCodeLocked(true);
    setForm(f => {
      const next = name === 'location_id'
        ? { ...f, location_id: value, rack_id: '' }
        : { ...f, [name]: value };
      if (name === 'name' && !isEdit && !codeLocked) next.code = value;
      return next;
    });
    setIsDirty(true);
    setApiError('');
  };

  const handleChange = e => setField(e.target.name, e.target.value);

  /**
   * Rellena la ficha con la plantilla del modelo del catálogo: número de
   * puertos, PoE y presupuesto. Solo toca los campos que aún están vacíos, así
   * que no pisa lo que el usuario (o el inventario) ya haya puesto.
   */
  const applyModelTemplate = (modelName) => {
    const option = (modelOptions || []).find(m => m.name === modelName);
    const spec = option && option.spec ? option.spec : null;
    if (!spec) return;
    setForm(prev => {
      const next = { ...prev };
      if (spec.port_count && !prev.port_count) next.port_count = String(spec.port_count);
      if (spec.poe_capable && !prev.poe_capable) next.poe_capable = true;
      if (spec.poe_budget_w && !prev.poe_budget_w) next.poe_budget_w = String(spec.poe_budget_w);
      return next;
    });
    setIsDirty(true);
  };

  const handlePortPreset = n => { setField('port_count', n); setCustomPorts(''); };

  const handleCustomPorts = e => {
    const v = e.target.value;
    setCustomPorts(v);
    const n = parseInt(v);
    if (!isNaN(n) && n > 0) setField('port_count', n);
  };

  const handleDiscard = () => {
    if (isEdit) {
      setForm({
        name: item.name || '', code: item.code || '',
        manufacturer: item.manufacturer || '', model: item.model || '',
        serial_number: item.serial_number || '', description: item.description || '',
        notes: item.notes || '', location_id: item.location_id ? String(item.location_id) : '',
        rack_id: item.rack_id ? String(item.rack_id) : '', size_u: item.size_u || 1,
        port_count: item.port_count || PORT_DEFAULT, status: item.status || 'active',
        poe_capable: !!item.poe_capable,
        poe_budget_w: item.poe_budget_w != null ? String(item.poe_budget_w) : '',
      });
    } else { onClose(); }
    setIsDirty(false);
    setApiError('');
    // Descartar en una ficha ya guardada vuelve a LECTURA (no la cierra).
    if (isEdit) setEditing(false);
  };

  const handleSubmit = async (e, { force = false, keepEditing = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    // `force`: guardar solo el nombre desde el título en reposo (Enter), sin
    // haber entrado antes en modo edición.
    if (!editing && !force) return false;
    setApiError('');
    if (!form.name.trim()) { setApiError('El nombre es obligatorio.'); return false; }
    if (elementType !== 'pdu' && (!form.port_count || Number(form.port_count) < 1)) { setApiError('El número de puertos debe ser mayor que 0.'); return false; }
    if (elementType === 'pdu'  && (!form.port_count || Number(form.port_count) < 1)) { setApiError('El número de enchufes debe ser mayor que 0.'); return false; }
    setSaving(true);
    try {
      const payload = {
        element_type:  elementType,
        name:          form.name.trim(),
        code:          form.code.trim()          || undefined,
        manufacturer:  form.manufacturer         || undefined,
        model:         form.model                || undefined,
        serial_number: form.serial_number        || undefined,
        description:   form.description          || undefined,
        notes:         form.notes                || undefined,
        rack_id:       form.rack_id              ? Number(form.rack_id)       : null,
        location_id:   form.location_id          ? Number(form.location_id)   : null,
        size_u:        Number(form.size_u)       || 1,
        status:        form.status,
      };
      // PDUs use num_outlets; panels/switches use port_count
      if (elementType === 'pdu') {
        payload.num_outlets = Number(form.port_count);
      } else {
        payload.port_count = Number(form.port_count);
      }
      // PoE (solo switches): capacidad de alimentar por Ethernet y presupuesto
      if (elementType === 'switch') {
        payload.poe_capable = !!form.poe_capable;
        payload.poe_budget_w = form.poe_budget_w !== '' && form.poe_budget_w != null
          ? Number(form.poe_budget_w)
          : null;
      }
      let creado = null;
      if (isEdit) await api.updateElement(item.id, payload, companyId);
      else        creado = registroGuardado(await api.createElement(payload, companyId));
      setIsDirty(false);
      setCommittedName(payload.name || '');
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      // Una ficha existente se queda abierta EN LECTURA tras guardar (salvo
      // que se esté pasando a la siguiente con «Guardar y continuar»).
      if (isEdit && !keepEditing) setEditing(false);
      return true;
    } catch (err) { setApiError(err.message || 'Error al guardar'); return false; }
    finally { setSaving(false); }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(false);
    api.deleteElement?.(item.id, companyId)
      .then(() => onClose?.())
      .catch(e => setApiError(e.message));
  };

  const openCloneModal = () => {
    setCloneName((item?.name || '') + ' (copia)');
    setShowCloneModal(true);
    // autofocus after render
    setTimeout(() => cloneNameRef.current?.focus(), 100);
  };

  const handleCloneConfirm = async () => {
    if (!item?.id || !cloneName.trim()) return;
    setCloning(true); setApiError('');
    try {
      await api.cloneElement(item.id, cloneName.trim(), companyId);
      setShowCloneModal(false);
      onSave?.();
    } catch (e) {
      setApiError(e.message || 'Error al clonar');
    } finally { setCloning(false); }
  };

  const handleSuggest = async () => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const resp = await api.suggestName({
        type: elementType, companyId,
        locationId: form.location_id || undefined,
        rackId: form.rack_id || undefined,
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) {
        // Consume reserva el número: dos clics seguidos nunca repiten código.
        setField('name', s);
        setIsDirty(true);
      }
    } catch (_) {}
    finally { setSuggesting(false); }
  };

  /**
   * Sugerir el CÓDIGO con la nomenclatura (PAN-0001, SWT-0004…). Es la acción
   * que la ficha canónica tiene pegada al campo Código; la de arriba sugiere el
   * nombre y se queda en la ficha clásica.
   */
  const handleSuggestCode = async () => {
    if (!companyId) return;
    setSuggesting(true);
    setApiError('');
    try {
      const resp = await api.suggestName({
        type: elementType, companyId,
        locationId: form.location_id || undefined,
        rackId: form.rack_id || undefined,
        consume: true,
      });
      const s = resp?.data?.suggested_name;
      if (s) {
        setCodeLocked(true);
        setForm(prev => ({ ...prev, code: s, name: prev.name || s }));
        setIsDirty(true);
      }
    } catch (err) {
      setApiError(err.message || 'No se pudo sugerir el código');
    } finally { setSuggesting(false); }
  };


  const loadMergeCandidates = useCallback(async () => {
    if (!companyId || !item?.id || elementType !== 'switch') return;
    setMergeCandidatesLoading(true);
    setMergeError('');
    try {
      const response = await api.getElements(companyId, { element_type: elementType });
      setMergeCandidates(response.data || []);
    } catch (e) {
      setMergeError(e.message || 'No se pudieron cargar los elementos candidatos');
    } finally {
      setMergeCandidatesLoading(false);
    }
  }, [companyId, item?.id, elementType]);

  const loadMergePreview = useCallback(async (targetId) => {
    if (!companyId || !item?.id || !targetId) {
      setMergePreview(null);
      setMergeResolutions({});
      setMergeRelationResolutions({});
      return;
    }

    setMergePreviewLoading(true);
    setMergeError('');
    try {
      const response = await api.previewCiMerge({
        source_type: 'rack_element',
        source_id: item.id,
        target_type: 'rack_element',
        target_id: Number(targetId),
      }, companyId);
      const preview = response.data || null;
      setMergePreview(preview);
      setMergeResolutions(buildDefaultMergeResolutions(preview?.field_preview));
      setMergeRelationResolutions({});
    } catch (e) {
      setMergePreview(null);
      setMergeResolutions({});
      setMergeRelationResolutions({});
      setMergeError(e.message || 'No se pudo generar el preview de fusión');
    } finally {
      setMergePreviewLoading(false);
    }
  }, [companyId, item?.id]);

  useEffect(() => {
    if (showMergePanel && isEdit && (elementType === 'switch' || elementType === 'panel')) {
      loadMergeCandidates();
    }
  }, [showMergePanel, isEdit, elementType, loadMergeCandidates]);

  // Auto-select the best candidate so the preview loads immediately
  useEffect(() => {
    if (filteredMergeCandidates.length > 0 && !mergeTargetId) {
      const bestId = String(filteredMergeCandidates[0].id);
      setMergeTargetId(bestId);
      loadMergePreview(bestId);
    }
  }, [filteredMergeCandidates, mergeTargetId, loadMergePreview]);

  const handleMergeTargetChange = async (event) => {
    const nextTargetId = event.target.value;
    setMergeTargetId(nextTargetId);
    setMergePreview(null);
    setMergeResolutions({});
    setMergeRelationResolutions({});
    setMergeResult(null);
    if (nextTargetId) {
      await loadMergePreview(nextTargetId);
    }
  };

  const handleMergeResolutionChange = (fieldName, choice) => {
    setMergeResolutions((prev) => ({
      ...prev,
      [fieldName]: choice,
    }));
  };

  const handleMergeSwitchPortsResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.owned_switch_ports) {
        const next = { ...prev };
        delete next.owned_switch_ports;
        return next;
      }
      return {
        ...prev,
        owned_switch_ports: {
          type: 'owned_switch_ports',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergePatchPortsResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.owned_patch_ports) {
        const next = { ...prev };
        delete next.owned_patch_ports;
        return next;
      }
      return {
        ...prev,
        owned_patch_ports: {
          type: 'owned_patch_ports',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergeIncomingSwitchLinksResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.incoming_switch_links) {
        const next = { ...prev };
        delete next.incoming_switch_links;
        return next;
      }
      return {
        ...prev,
        incoming_switch_links: {
          type: 'incoming_switch_links',
          strategy: 'move',
        },
      };
    });
  };

  const handleMergeIncomingPatchLinksResolutionChange = () => {
    setMergeRelationResolutions((prev) => {
      if (prev.incoming_patch_links) {
        const next = { ...prev };
        delete next.incoming_patch_links;
        return next;
      }
      return {
        ...prev,
        incoming_patch_links: {
          type: 'incoming_patch_links',
          strategy: 'move',
        },
      };
    });
  };

  const handleExecuteMerge = async () => {
    if (!companyId || !item?.id || !mergeTargetId || !canExecuteMerge) return;
    const targetLabel = selectedMergeCandidate?.name || mergePreview?.target?.name || `CI ${mergeTargetId}`;
    if (!window.confirm(`Se va a fusionar este elemento dentro de "${targetLabel}". El origen quedará archivado como merged. ¿Continuar?`)) {
      return;
    }

    setMergeExecuting(true);
    setMergeError('');
    setMergeResult(null);
    try {
      await api.executeCiMerge({
        source_type: 'rack_element',
        source_id: item.id,
        target_type: 'rack_element',
        target_id: Number(mergeTargetId),
        field_resolutions: mergeResolutions,
        relation_resolutions: Object.values(mergeRelationResolutions),
      }, companyId);
      setMergeResult({
        targetId: Number(mergeTargetId),
        targetLabel,
      });
      await onSave?.();
    } catch (e) {
      setMergeError(e.message || 'No se pudo ejecutar la fusión');
    } finally {
      setMergeExecuting(false);
    }
  };

  const handleOpenMergedTarget = () => {
    if (!selectedMergeCandidate || !onNavigate) return;
    onNavigate(selectedMergeCandidate);
  };

  const statusColors = {
    active:      { bg: '#dcfce7', color: '#15803d', label: 'Activo' },
    maintenance: { bg: '#fef9c3', color: '#854d0e', label: 'Mantenimiento' },
    offline:     { bg: '#fee2e2', color: '#991b1b', label: 'Fuera de servicio' },
  };
  const currentStatus = statusColors[form.status] || statusColors.active;

  // Nombre pendiente de guardar: el título en línea se compara con el último
  // nombre guardado, no con la fila del listado.
  const nameDirty = (form.name || '') !== committedName;

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty || nameDirty,
    saving,
    entity: 'este elemento',
    actionLabel: 'pasas a otro elemento o cierras la ficha',
    save: () => handleSubmit(null, { force: true, keepEditing: editing }),
  });

  /**
   * Pasar al elemento anterior/siguiente desde el paginador. Si hay cambios sin
   * guardar se pide confirmación, como en la ficha canónica del rack.
   */
  const irANav = (destino) => {
    if (!destino || saving) return;
    navGuard.request(() => {
      setIsDirty(false);
      setApiError('');
      onNavigate?.(destino);
    });
  };

  // Ocupación de puertos (paneles y switches): un puerto está ocupado si tiene
  // cable o uplink, con el mismo criterio que la pestaña de puertos.
  const puertosTotal = Number(form.port_count) || 0;
  const puertosOcupados = ports.filter(p => (
    (p.connection || (p.destination_type === 'uplink' && p.connected_to))
    && p.is_active !== false
  )).length;
  const puertosLibres = Math.max(0, puertosTotal - puertosOcupados);
  const ocupacionPct = puertosTotal > 0 ? Math.round((puertosOcupados / puertosTotal) * 100) : 0;

  const tabs = [
    { key: 'identificacion', label: 'Identificación' },
    { key: 'ubicacion',      label: 'Ubicación' },
    elementType === 'pdu'
      ? { key: 'enchufes', label: `Enchufes (${form.port_count})` }
      : { key: 'puertos',  label: `Puertos (${form.port_count})` },
    // El diagrama «desde el punto de vista del panel/switch»: sus puertos y la
    // cadena física de cada uno (toma de pared, latiguillo, switch…).
    ...(elementType !== 'pdu' ? [{ key: 'conexiones', label: 'Conexiones' }] : []),
    { key: 'hardware',       label: 'Hardware' },
    { key: 'red',            label: `Red / IP${ipCount > 0 ? ` (${ipCount})` : ''}` },
    ...(elementType !== 'panel'
      ? [{ key: 'access', label: `Accesos${accessCount > 0 ? ` (${accessCount})` : ''}` }]
      : []),
    ...(elementType === 'switch' || elementType === 'pdu'
      ? [{ key: 'power', label: '⚡ Alimentación' }]
      : []),
  ];

  // En tarjetas, Identificación y Ubicación se editan en el Resumen.
  const tabsDef = isCards ? tabs.filter((tab) => tab.key !== 'identificacion' && tab.key !== 'ubicacion') : tabs;
  // En tarjetas el hardware vive en la tarjeta de identificación (fabricante,
  // modelo, nº de serie): la pestaña sería el mismo dato dos veces (§12, un
  // dato un sitio). Se quita también en el switch.
  const tabsSinDuplicados = isCards
    ? tabsDef.filter((tab) => tab.key !== 'hardware')
    : tabsDef;
  const tabsFinal = isCards ? [{ key: 'general', label: 'General' }, ...tabsSinDuplicados] : tabsDef;
  const paneClass = `dv-tab-pane${isCards ? ' sf2-pane' : ''}`;
  const iconoElemento = elementType === 'switch'
    ? <ArrowsRightLeftIcon style={{ width: 20, height: 20 }} />
    : elementType === 'pdu'
      ? <BoltIcon style={{ width: 20, height: 20 }} />
      : <TableCellsIcon style={{ width: 20, height: 20 }} />;
  const mapaUbicacionesForm = useMemo(() => buildLocationMap(locations), [locations]);
  const rutaUbicacionForm = form.location_id ? locationPath(mapaUbicacionesForm, form.location_id) : '';
  const tabPuertosKey = elementType === 'pdu' ? 'enchufes' : 'puertos';
  const tabPuertosLabel = elementType === 'pdu' ? 'Enchufes' : 'Puertos';

  return (
    <div className="dv-overlay">

      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          {lbl.plural || lbl.singular}
        </button>
        <DetailCrumb label={form.name} />
        {/* Contador de fichas: SIEMPRE a la izquierda, pegado a la migaja, antes
            del espaciador. A la derecha (junto a las acciones) es un error.
            Ver §3.1 del estándar. */}
        <FormPager
          items={navItems || []}
          index={navIndex}
          label="Elemento"
          disabled={saving}
          onChange={irANav}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {apiError && (
          <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            ⚠ {apiError}
          </span>
        )}
        <div className="dv-topbar-actions">
          {isCards ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon />
                Cerrar
              </button>
              {/* En tarjetas el lápiz del título sirve para renombrar en línea;
                  para editar el resto de campos hace falta este botón, igual
                  que en la ficha canónica del rack. */}
              {isEdit && !editing && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
                  <PencilSquareIcon />
                  Editar
                </button>
              )}
              {isEdit && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={openCloneModal}
                  disabled={cloning || saving}
                  title="Crear una copia sin número de serie ni conexiones"
                >
                  {cloning ? <span className="dv-spinner" /> : <DocumentDuplicateIcon />}
                  {cloning ? 'Clonando…' : 'Clonar'}
                </button>
              )}
              {isEdit && (
                <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
                  <TrashIcon />
                </button>
              )}
            </>
          ) : (
          <>
          <span className="dv-status-badge" style={{ background: currentStatus.bg, color: currentStatus.color }}>
            ● {currentStatus.label}
          </span>

          {/* En LECTURA: editar (y copiar/fusionar). En EDICIÓN: descartar y guardar. */}
          {!editing && isEdit && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)} disabled={saving}>
              <PencilSquareIcon />
              Editar
            </button>
          )}
          {editing && isEdit && (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={handleDiscard} disabled={saving}>
              <XMarkIcon />
              Descartar
            </button>
          )}
          {isEdit && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={openCloneModal}
              disabled={cloning || saving}
              title={`Crear una copia sin número de serie ni conexiones`}
            >
              {cloning ? <span className="dv-spinner" /> : <DocumentDuplicateIcon />}
              {cloning ? 'Clonando…' : 'Clonar'}
            </button>
          )}
          {editing && isEdit && (elementType === 'switch' || elementType === 'panel') && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={() => setShowMergePanel((value) => !value)}
              disabled={saving || mergeExecuting}
            >
              {showMergePanel ? 'Ocultar fusión' : 'Fusionar'}
            </button>
          )}
          </>
          )}

          {/* ── Clone Modal ── */}
          <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>
                <DocumentDuplicateIcon style={{ width: 18, height: 18, marginRight: 8 }} />
                Clonar {elementType === 'pdu' ? 'PDU' : elementType === 'panel' ? 'patch panel' : 'switch'}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Form.Group>
                <Form.Label className="fw-semibold">Nombre del clon</Form.Label>
                <Form.Control
                  ref={cloneNameRef}
                  type="text"
                  value={cloneName}
                  onChange={e => setCloneName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCloneConfirm(); }}
                  placeholder={`Nombre del nuevo ${elementType === 'pdu' ? 'PDU' : elementType === 'panel' ? 'patch panel' : 'switch'}…`}
                  autoFocus
                />
                <Form.Text className="text-muted">
                  {elementType === 'pdu'
                    ? 'Se copiarán todos los enchufes. No se copiará número de serie, IP ni conexiones.'
                    : elementType === 'switch'
                      ? 'Se copiarán los puertos (con su PoE), la capacidad PoE y las fuentes de alimentación. No se copiará el número de serie, la IP ni las conexiones.'
                      : 'Se copiarán todos los puertos. No se copiará número de serie, IP ni conexiones.'}
                </Form.Text>
              </Form.Group>
              {apiError && <div className="alert alert-danger mt-3 mb-0 py-2">{apiError}</div>}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowCloneModal(false)} disabled={cloning}>
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleCloneConfirm}
                disabled={cloning || !cloneName.trim()}
              >
                {cloning ? '⏳ Clonando…' : '📋 Clonar'}
              </Button>
            </Modal.Footer>
          </Modal>
          {!isCards && (
          <>
          <button
            className="dv-btn dv-btn-primary"
            type="button"
            onClick={handleSubmit}
            disabled={!editing || saving || !isDirty}
          >
            {saving ? <span className="dv-spinner" /> : <CheckIcon />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          {isEdit && (
            <button className="dv-btn dv-btn-danger" type="button" onClick={() => setShowDeleteConfirm(true)} disabled={saving}>
              <TrashIcon />
            </button>
          )}
          </>
          )}
        </div>
      </div>

      {showMergePanel && editing && isEdit && (elementType === 'switch' || elementType === 'panel') && (
        <div style={{
          margin: '0 1.5rem 0.75rem',
          padding: '1rem 1.25rem',
          borderRadius: 10,
          border: '1px solid rgba(180,83,9,.25)',
          background: 'linear-gradient(135deg, rgba(245,158,11,.14), rgba(251,191,36,.06))',
          color: 'var(--text-primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.75rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b45309', fontWeight: 700, marginBottom: 2 }}>
                {elementType === 'switch' ? 'Fusión manual de switches' : 'Fusión manual de patch panels'}
              </div>
              <div style={{ fontSize: '0.95rem', fontWeight: 700 }}>
                Selecciona el {elementType === 'switch' ? 'switch' : 'patch panel'} destino, revisa el preview y confirma solo si no quedan bloqueos abiertos.
              </div>
              <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Esta pantalla ya puede desbloquear el movimiento manual de puertos propios del {elementType === 'switch' ? 'switch' : 'patch panel'}. Otros enlaces físicos siguen protegidos hasta completar sus resoluciones específicas.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowMergePanel(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}
            >✕</button>
          </div>

          {mergeError && (
            <div style={{
              marginBottom: 12,
              padding: '0.7rem 0.9rem',
              borderRadius: 8,
              background: 'rgba(220,38,38,.08)',
              border: '1px solid rgba(220,38,38,.2)',
              color: '#b91c1c',
              fontSize: '0.85rem',
            }}>
              {mergeError}
            </div>
          )}

          {mergeResult && (
            <div style={{
              marginBottom: 12,
              padding: '0.8rem 0.95rem',
              borderRadius: 8,
              background: 'rgba(16,185,129,.08)',
              border: '1px solid rgba(16,185,129,.2)',
              color: '#047857',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}>
              <div>
                <div style={{ fontWeight: 700 }}>Fusión completada</div>
                <div style={{ fontSize: '0.84rem' }}>
                  El origen ha quedado archivado como `merged` y el switch superviviente es {mergeResult.targetLabel}.
                </div>
              </div>
              {selectedMergeCandidate && onNavigate && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={handleOpenMergedTarget}
                >
                  Abrir destino
                </button>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
            <div style={{ display: 'grid', gap: 10 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Buscar destino
                </label>
                <input
                  type="text"
                  value={mergeSearch}
                  onChange={(event) => setMergeSearch(event.target.value)}
                  placeholder="Nombre, código, serie, rack..."
                  style={{
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '0.55rem 0.7rem',
                    fontSize: '0.84rem',
                    background: 'var(--bg-secondary, #1e1e2e)',
                    color: 'var(--text-primary)',
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  Switch destino
                </label>
                <select
                  value={mergeTargetId}
                  onChange={handleMergeTargetChange}
                  size={Math.min(Math.max(filteredMergeCandidates.length, 2), 8)}
                  style={{
                    width: '100%',
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: '0.35rem',
                    fontSize: '0.84rem',
                    background: 'var(--bg-secondary, #1e1e2e)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {mergeCandidatesLoading ? (
                    <option value="">Cargando candidatos...</option>
                  ) : filteredMergeCandidates.length === 0 ? (
                    <option value="">No hay candidatos visibles</option>
                  ) : (
                    filteredMergeCandidates.map((candidate) => {
                      const score = buildElementMergeCandidateScore(item, candidate);
                      const suffix = [candidate.code, candidate.rack_name].filter(Boolean).join(' · ');
                      return (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.name || `CI ${candidate.id}`}{suffix ? ` · ${suffix}` : ''}{score > 0 ? ` · score ${score}` : ''}
                        </option>
                      );
                    })
                  )}
                </select>
              </div>

              {selectedMergeCandidate && (
                <div style={{
                  padding: '0.8rem 0.9rem',
                  borderRadius: 8,
                  background: 'var(--bg-tertiary, rgba(255,255,255,.08))',
                  border: '1px solid rgba(180,83,9,.18)',
                  fontSize: '0.82rem',
                  display: 'grid',
                  gap: 4,
                }}>
                  <div style={{ fontWeight: 700 }}>{selectedMergeCandidate.name || `CI ${selectedMergeCandidate.id}`}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{selectedMergeCandidate.code || 'Sin código'}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{[selectedMergeCandidate.manufacturer, selectedMergeCandidate.model].filter(Boolean).join(' ') || 'Sin fabricante/modelo'}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>
                    {selectedMergeCandidate.location_name || 'Sin ubicación'}{selectedMergeCandidate.rack_name ? ` · ${selectedMergeCandidate.rack_name}` : ''}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={loadMergeCandidates}
                  disabled={mergeCandidatesLoading}
                >
                  {mergeCandidatesLoading ? 'Recargando...' : 'Recargar lista'}
                </button>
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={() => loadMergePreview(mergeTargetId)}
                  disabled={!mergeTargetId || mergePreviewLoading}
                >
                  {mergePreviewLoading ? 'Calculando...' : 'Refrescar preview'}
                </button>
              </div>
            </div>

            <div style={{
              minHeight: 160,
              maxHeight: 'calc(100vh - 260px)',
              overflowY: 'auto',
              padding: '0.9rem 1rem',
              borderRadius: 8,
              background: 'var(--bg-tertiary, rgba(255,255,255,.08))',
              border: '1px solid rgba(180,83,9,.16)',
            }}>
              {!mergeTargetId ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
                  Selecciona un {elementType === 'switch' ? 'switch' : 'patch panel'} destino para generar el preview de fusión.
                </div>
              ) : mergePreviewLoading ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
                  Calculando diferencias y relaciones a mover...
                </div>
              ) : mergePreview ? (
                <div style={{ display: 'grid', gap: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                        Preview
                      </div>
                      <div style={{ fontWeight: 700 }}>
                        {mergePreview.source?.name || `CI ${mergePreview.source?.id}`} → {mergePreview.target?.name || `CI ${mergePreview.target?.id}`}
                      </div>
                    </div>
                    <div style={{
                      padding: '0.35rem 0.7rem',
                      borderRadius: 999,
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      background: canExecuteMerge ? 'rgba(16,185,129,.12)' : 'rgba(220,38,38,.1)',
                      color: canExecuteMerge ? '#047857' : '#b91c1c',
                    }}>
                      {canExecuteMerge ? 'Listo para ejecutar' : 'Bloqueado'}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Campos a completar</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0369a1' }}>{mergePreview.field_summary?.take_source || 0}</div>
                    </div>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Conflictos de campo</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#b45309' }}>{mergePreview.field_summary?.conflicts || 0}</div>
                    </div>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Movimientos automáticos</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#047857' }}>{mergePreview.relation_summary?.automatic_moves || 0}</div>
                    </div>
                    <div style={{ padding: '0.7rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)' }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Bloqueos</div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: mergeUnresolvedBlocks.length ? '#b91c1c' : '#6b7280' }}>{mergeUnresolvedBlocks.length}</div>
                    </div>
                  </div>

                  {mergeConflictFields.length > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Conflictos de identidad</div>
                      {mergeConflictFields.map((previewItem) => (
                        <div key={previewItem.field} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.8rem 0.9rem', display: 'grid', gap: 8 }}>
                          <div style={{ fontWeight: 700 }}>{previewItem.label}</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
                            <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.6rem 0.7rem', cursor: 'pointer', background: mergeResolutions[previewItem.field] === 'target' ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                <input
                                  type="radio"
                                  name={`merge-${previewItem.field}`}
                                  checked={mergeResolutions[previewItem.field] === 'target'}
                                  onChange={() => handleMergeResolutionChange(previewItem.field, 'target')}
                                />
                                <span style={{ fontWeight: 700 }}>Conservar destino</span>
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeValue(previewItem.target_value)}</div>
                            </label>
                            <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.6rem 0.7rem', cursor: 'pointer', background: mergeResolutions[previewItem.field] === 'source' ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                <input
                                  type="radio"
                                  name={`merge-${previewItem.field}`}
                                  checked={mergeResolutions[previewItem.field] === 'source'}
                                  onChange={() => handleMergeResolutionChange(previewItem.field, 'source')}
                                />
                                <span style={{ fontWeight: 700 }}>Tomar origen</span>
                              </div>
                              <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeValue(previewItem.source_value)}</div>
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'grid', gap: 8 }}>
                    <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Relaciones afectadas</div>
                    {mergeMovableRelations.length === 0 ? (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.84rem' }}>No hay relaciones activas en el origen para mover.</div>
                    ) : (
                      mergeMovableRelations.map((previewItem) => (
                        <div key={previewItem.key} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '0.65rem 0.8rem', borderRadius: 8, background: 'var(--slate-50)', border: '1px solid var(--border)' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{previewItem.label}</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                              Origen: {previewItem.source_count} · Destino: {previewItem.target_count}
                            </div>
                          </div>
                          <div style={{
                            fontSize: '0.78rem',
                            fontWeight: 700,
                            color: previewItem.action === 'move' ? '#047857' : '#b45309',
                          }}>
                            {previewItem.action === 'move' ? 'Automático' : 'Revisión manual'}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {elementType === 'switch' && mergeSwitchPortsRelation && mergeSwitchPortsRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de puertos propios del switch</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna la propiedad de los puertos del switch origen al destino. Solo se permite si el destino no usa ya esos mismos números de puerto.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Puertos del origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergeSwitchPortsRelation.details?.source_ports)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Puertos del destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergeSwitchPortsRelation.details?.target_ports)}</div>
                          </div>
                        </div>
                        {Array.isArray(mergeSwitchPortsRelation.details?.overlapping_ports) && mergeSwitchPortsRelation.details.overlapping_ports.length > 0 && (
                          <div style={{ border: '1px solid rgba(220,38,38,.15)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'rgba(220,38,38,.05)', color: '#991b1b', fontSize: '0.82rem' }}>
                            Números de puerto ya ocupados en el destino: {formatMergeList(mergeSwitchPortsRelation.details.overlapping_ports)}
                          </div>
                        )}
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.owned_switch_ports ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.owned_switch_ports)}
                              onChange={handleMergeSwitchPortsResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Mover puertos propios del switch al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            El backend conserva el número de puerto y rechaza el merge si detecta colisión real en destino.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {elementType === 'panel' && mergePatchPortsRelation && mergePatchPortsRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de puertos propios del patch panel</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna la propiedad de los puertos del patch panel origen al destino. Solo se permite si el destino no usa ya esos mismos números de puerto.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Puertos del origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergePatchPortsRelation.details?.source_ports)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Puertos del destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergePatchPortsRelation.details?.target_ports)}</div>
                          </div>
                        </div>
                        {Array.isArray(mergePatchPortsRelation.details?.overlapping_ports) && mergePatchPortsRelation.details.overlapping_ports.length > 0 && (
                          <div style={{ border: '1px solid rgba(220,38,38,.15)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'rgba(220,38,38,.05)', color: '#991b1b', fontSize: '0.82rem' }}>
                            Números de puerto ya ocupados en el destino: {formatMergeList(mergePatchPortsRelation.details.overlapping_ports)}
                          </div>
                        )}
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.owned_patch_ports ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.owned_patch_ports)}
                              onChange={handleMergePatchPortsResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Mover puertos propios del patch panel al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            El backend conserva el número de puerto y rechaza el merge si detecta colisión real en destino.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {mergeIncomingSwitchLinksRelation && mergeIncomingSwitchLinksRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de enlaces entrantes desde switches</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna al elemento destino los puertos de switch que ahora apuntan al origen. El cableado físico sigue intacto; solo cambia la referencia lógica del elemento conectado.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que apuntan al origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergeIncomingSwitchLinksRelation.details?.source_links)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que ya apuntan al destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergeIncomingSwitchLinksRelation.details?.target_links)}</div>
                          </div>
                        </div>
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.incoming_switch_links ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.incoming_switch_links)}
                              onChange={handleMergeIncomingSwitchLinksResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Reasignar enlaces entrantes al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Úsalo cuando el elemento origen es un duplicado y quieres conservar sobre el destino el mismo enlace lógico desde puertos de switch.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {mergeIncomingPatchLinksRelation && mergeIncomingPatchLinksRelation.source_count > 0 && (
                    <div style={{ display: 'grid', gap: 10 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700 }}>Resolución manual de enlaces entrantes desde patch panels</div>
                      <div style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.85rem 0.95rem', background: 'rgba(245,158,11,.05)', display: 'grid', gap: 10 }}>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                          Esta acción reasigna al elemento destino los puertos de patch panel que ahora apuntan al origen. El cableado físico sigue intacto; solo cambia la referencia lógica del elemento conectado.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que apuntan al origen</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergeIncomingPatchLinksRelation.details?.source_links)}</div>
                          </div>
                          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '0.7rem 0.8rem', background: 'var(--bg-tertiary, rgba(255,255,255,.08))' }}>
                            <div style={{ fontWeight: 700, marginBottom: 4 }}>Enlaces que ya apuntan al destino</div>
                            <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{formatMergeList(mergeIncomingPatchLinksRelation.details?.target_links)}</div>
                          </div>
                        </div>
                        <label style={{ border: '1px solid rgba(180,83,9,.18)', borderRadius: 8, padding: '0.7rem 0.8rem', cursor: 'pointer', background: mergeRelationResolutions.incoming_patch_links ? 'rgba(245,158,11,.08)' : 'transparent' }}>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(mergeRelationResolutions.incoming_patch_links)}
                              onChange={handleMergeIncomingPatchLinksResolutionChange}
                            />
                            <span style={{ fontWeight: 700 }}>Reasignar enlaces entrantes al destino</span>
                          </div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                            Úsalo cuando el elemento origen es un duplicado y quieres conservar sobre el destino el mismo enlace lógico desde puertos de patch panel.
                          </div>
                        </label>
                      </div>
                    </div>
                  )}

                  {Array.isArray(mergeUnresolvedBlocks) && mergeUnresolvedBlocks.length > 0 && (
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div style={{ fontSize: '0.84rem', fontWeight: 700, color: '#b91c1c' }}>Bloqueos detectados</div>
                      {mergeUnresolvedBlocks.map((block) => (
                        <div key={block.key} style={{ padding: '0.75rem 0.85rem', borderRadius: 8, background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.15)' }}>
                          <div style={{ fontWeight: 700, color: '#991b1b' }}>{block.label}</div>
                          <div style={{ color: '#b91c1c', fontSize: '0.82rem' }}>{block.reason}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4 }}>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                      Si no cambias un conflicto de campo, el backend conserva el valor del destino.
                    </div>
                    <button
                      className="dv-btn dv-btn-primary"
                      type="button"
                      onClick={handleExecuteMerge}
                      disabled={!canExecuteMerge || mergeExecuting}
                    >
                      {mergeExecuting ? 'Fusionando...' : 'Ejecutar fusión'}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('puertos')}>
          <span className="dv-smart-btn-value" style={{ color: '#2563eb' }}>{form.port_count}</span>
          <span className="dv-smart-btn-label">Puertos</span>
        </button>

        {form.location_id && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
            <span className="dv-smart-btn-icon">
              <MapPinIcon style={{ width: 18, height: 18, color: '#0369a1' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#0369a1', fontSize: '0.85rem' }}>
              {selectedLocation?.name || '—'}
            </span>
            <span className="dv-smart-btn-label">Ubicación</span>
          </button>
        )}

        {form.rack_id && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('ubicacion')}>
            <span className="dv-smart-btn-icon">
              <CubeIcon style={{ width: 18, height: 18, color: '#7c3aed' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.85rem' }}>
              {selectedRack?.name || selectedRack?.code || 'Rack'}
            </span>
            <span className="dv-smart-btn-label">Rack</span>
          </button>
        )}

        {(form.manufacturer || form.model) && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('hardware')}>
            <span className="dv-smart-btn-value" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              {[form.manufacturer, form.model].filter(Boolean).join(' ')}
            </span>
            <span className="dv-smart-btn-label">Fabricante / Modelo</span>
          </button>
        )}

        {/* PoE (solo switches) */}
        {elementType === 'switch' && form.poe_capable && (
          <button className="dv-smart-btn" type="button" onClick={() => setActiveTab('power')}>
            <span className="dv-smart-btn-icon">
              <BoltIcon style={{ width: 18, height: 18, color: '#d97706' }} />
            </span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706' }}>
              {form.poe_budget_w ? `${form.poe_budget_w} W` : 'Sí'}
            </span>
            <span className="dv-smart-btn-label">PoE</span>
          </button>
        )}

        {/* Documentación vinculada (switch / panel / pdu) */}
        {isEdit && item?.id && (
          <DocSmartButton
            elementType={elementType}
            elementId={item.id}
            elementName={item.name || form.name}
            companyId={companyId}
          />
        )}
      </div>
      )}

      {/* ── Body ── */}
      <div className="dv-body">
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>
          {/* En LECTURA el fieldset desactiva de golpe todos los campos,
              selects, textareas y botones internos (nativo de HTML). En
              tarjetas el fieldset exterior queda activo y el bloqueo lo hace
              el interior. */}
          <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={iconoElemento}
                title={form.name}
                onTitleChange={(v) => setField('name', v)}
                placeholder={`Nombre del ${lbl.singular.toLowerCase()}…`}
                editing={editing}
                saving={saving}
                onSave={handleSubmit}
                onCancel={handleDiscard}
                onEdit={() => setEditing(true)}
                dirty={isDirty || nameDirty}
                titleDirty={nameDirty}
                onTitleCommit={() => handleSubmit(null, { force: true })}
                onTitleReset={handleDiscard}
                crumb={[lbl.singular, form.code, rutaUbicacionForm, selectedRack?.name || selectedRack?.code].filter(Boolean).join(' › ')}
                badges={[
                  {
                    text: currentStatus.label,
                    tone: form.status === 'active' ? 'ok' : form.status === 'maintenance' ? 'warn' : 'bad',
                  },
                  ...(form.rack_id ? [{ text: selectedRack?.name || selectedRack?.code || 'Rack' }] : []),
                  ...(elementType === 'switch' && form.poe_capable ? [{ text: form.poe_budget_w ? `PoE ${form.poe_budget_w} W` : 'PoE' }] : []),
                ]}
                chips={[
                  { value: form.port_count, label: elementType === 'pdu' ? 'enchufes' : 'puertos', onClick: () => setActiveTab(tabPuertosKey) },
                  { value: ipCount, label: 'IPs', onClick: () => setActiveTab('red') },
                  ...(elementType !== 'panel' && accessCount > 0 ? [{ value: accessCount, label: 'accesos', onClick: () => setActiveTab('access') }] : []),
                ]}
                extra={isEdit && item?.id ? (
                  <DocSmartButton elementType={elementType} elementId={item.id} elementName={item.name || form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    className="dv-record-title"
                    style={{ flex: 1 }}
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder={`Nombre del ${lbl.singular.toLowerCase()}…`}
                    required
                    autoFocus={!isEdit}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleSuggest}
                    disabled={suggesting}
                    title="Sugerir nombre automático"
                    style={{ flexShrink: 0, padding: '4px 10px' }}
                  >
                    {suggesting ? '…' : <SparklesIcon style={{ width: 16, height: 16 }} />}
                  </button>
                </div>
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {tabsFinal.map(tab => (
                  <TabButton
                    key={tab.key}
                    type="button"
                    className={`dv-tab-btn${activeTab === tab.key ? ' active' : ''}`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
          <fieldset disabled={!editing} className="dv-fieldset">

          {/* ── Tab: Resumen (solo tarjetas) ── */}
          {isCards && activeTab === 'general' && (
            <div className={paneClass}>
              {elementType === 'pdu' ? (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>{tabPuertosLabel}</span><b>{form.port_count}</b></div>
                  <div className="sf2-kpi"><span>IPs</span><b>{ipCount}</b></div>
                  <div className="sf2-kpi"><span>Accesos</span><b>{accessCount}</b></div>
                  <div className="sf2-kpi"><span>Rack</span><b style={{ fontSize: '0.95rem' }}>{selectedRack?.name || selectedRack?.code || '—'}</b></div>
                  <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{currentStatus.label}</b></div>
                </div>
              ) : (
                /* Ocupación de puertos: mismo bloque que la ficha canónica del
                   rack, cambiando U por puertos. */
                <div className="sf2-card" style={{ marginBottom: 12 }}>
                  <div className="sf2-occ">
                    <div className="sf2-occ-top">
                      <b className="sf2-occ-title">
                        {puertosOcupados} de {puertosTotal} {tabPuertosLabel.toLowerCase()} ocupados
                      </b>
                      <span className="sf2-occ-pct">{ocupacionPct} % de ocupación</span>
                    </div>
                    {puertosTotal > 60 ? (
                      <div
                        className="sf2-occ-track"
                        role="img"
                        aria-label={`${puertosOcupados} de ${puertosTotal} ${tabPuertosLabel.toLowerCase()} ocupados`}
                      >
                        <span className="sf2-occ-fill" style={{ width: `${ocupacionPct}%` }} />
                      </div>
                    ) : (
                      <div
                        className="sf2-occ-bar"
                        role="img"
                        aria-label={`${puertosOcupados} de ${puertosTotal} ${tabPuertosLabel.toLowerCase()} ocupados`}
                      >
                        {Array.from({ length: puertosTotal || 0 }, (_, i) => (
                          <span
                            key={i}
                            className={`sf2-occ-seg${i < puertosOcupados ? ' is-on' : ''}`}
                            title={`${tabPuertosLabel.slice(0, -1)} ${i + 1} · ${i < puertosOcupados ? 'ocupado' : 'libre'}`}
                          />
                        ))}
                      </div>
                    )}
                    <div className="sf2-occ-legend">
                      <span><b>{puertosLibres}</b> libres</span>
                      <span><b>{ipCount}</b> IPs</span>
                      <span>Rack <b>{selectedRack?.name || selectedRack?.code || '—'}</b></span>
                      <span
                        className="sf2-link"
                        role="button"
                        tabIndex={0}
                        onClick={() => setActiveTab(tabPuertosKey)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(tabPuertosKey); } }}
                      >Ver {tabPuertosLabel.toLowerCase()} →</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="sf2-grid sf2-grid--2">
                <div className="sf2-card">
                  <h3>Identificación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>Código</label>
                      {editing ? (
                        /* El generador va pegado al campo, no en la cabecera
                           (§12.C8). La acción de la cabecera sugiere el nombre. */
                        <div className="sf2-input-row">
                          <input name="code" value={form.code} onChange={handleChange} placeholder={elementType === 'panel' ? 'PP-SALA-A-01' : elementType === 'pdu' ? 'PDU-RACK-01' : 'SW-CORE-01'} />
                          <button
                            type="button"
                            className="sf2-icon-btn"
                            onClick={handleSuggestCode}
                            disabled={suggesting || !companyId || saving}
                            title="Generar el código con la nomenclatura"
                            aria-label="Generar código"
                          >
                            {suggesting ? <span className="dv-spinner" /> : <SparklesIcon style={{ width: 15, height: 15 }} />}
                          </button>
                        </div>
                      ) : <div className="v">{form.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Estado</label>
                      {editing ? (
                        <select name="status" value={form.status} onChange={handleChange}>
                          <option value="active">Activo</option>
                          <option value="maintenance">Mantenimiento</option>
                          <option value="offline">Fuera de servicio</option>
                        </select>
                      ) : <div className="v">{currentStatus.label}</div>}
                    </div>
                    {/* Un solo control para el estado: el select. Tuvo también un
                        interruptor «En servicio» y sobraba: duplicaba el mismo dato
                        y encima dejaba dos formas de decir lo mismo. */}
                    <div className="sf2-field">
                      <label>Fabricante</label>
                      {editing
                        ? (
                          elementType === 'switch' ? (
                            <>
                              <select
                                name="manufacturer"
                                value={form.manufacturer}
                                onChange={(e) => { setField('manufacturer', e.target.value); setField('model', ''); }}
                              >
                                <option value="">— Seleccionar —</option>
                                {manufacturerOptions.map((opt) => (
                                  <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                                ))}
                              </select>
                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <input
                                  value={newManufacturer}
                                  onChange={(e) => setNewManufacturer(e.target.value)}
                                  placeholder="Nueva marca…"
                                  style={{ flex: 1 }}
                                />
                                <button
                                  type="button"
                                  className="list-btn-secondary"
                                  style={{ padding: '3px 9px', fontSize: '0.74rem' }}
                                  title="Crear la marca en el catálogo y seleccionarla"
                                  onClick={async () => {
                                    const name = newManufacturer.trim();
                                    if (!name) return;
                                    setField('manufacturer', name);
                                    setField('model', '');
                                    await saveManufacturerToCatalog(name);
                                    setNewManufacturer('');
                                  }}
                                >
                                  + Marca
                                </button>
                              </div>
                            </>
                          ) : (
                            <input
                              name="manufacturer"
                              value={form.manufacturer}
                              onChange={handleChange}
                              placeholder="Cisco, Panduit…"
                            />
                          )
                        )
                        : <div className="v">{form.manufacturer || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Modelo</label>
                      {editing
                        ? (
                          elementType === 'switch' ? (
                            <>
                              <select
                                name="model"
                                value={form.model}
                                onChange={(e) => { handleChange(e); applyModelTemplate(e.target.value); }}
                                disabled={!form.manufacturer && filteredModelOptions.length === 0}
                              >
                                <option value="">— Seleccionar —</option>
                                {filteredModelOptions.map((opt) => (
                                  <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                                ))}
                              </select>
                              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                <input
                                  value={newModel}
                                  onChange={(e) => setNewModel(e.target.value)}
                                  placeholder="Nuevo modelo…"
                                  style={{ flex: 1 }}
                                />
                                <button
                                  type="button"
                                  className="list-btn-secondary"
                                  style={{ padding: '3px 9px', fontSize: '0.74rem' }}
                                  title="Crear el modelo en el catálogo (con la marca elegida) y seleccionarlo"
                                  onClick={async () => {
                                    const name = newModel.trim();
                                    if (!name) return;
                                    setField('model', name);
                                    await saveModelToCatalog(name);
                                    setNewModel('');
                                  }}
                                >
                                  + Modelo
                                </button>
                              </div>
                              <a
                                href="/admin/catalog"
                                target="_blank"
                                rel="noreferrer"
                                className="list-hint"
                                style={{ display: 'inline-block', marginTop: 5, fontSize: '0.72rem' }}
                              >
                                Abrir el catálogo para completar sus características →
                              </a>
                            </>
                          ) : (
                            <input name="model" value={form.model} onChange={handleChange} placeholder="Modelo" />
                          )
                        )
                        : <div className="v">{form.model || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Nº de serie</label>
                      {editing
                        ? <input name="serial_number" value={form.serial_number} onChange={handleChange} placeholder="S/N" />
                        : <div className="v">{form.serial_number || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>{elementType === 'pdu' ? 'Enchufes' : 'Puertos'}</label>
                      {editing
                        ? <input type="number" min="1" max="96" name="port_count" value={form.port_count} onChange={handleChange} />
                        : <div className="v">{form.port_count}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Descripción / Notas</label>
                      {editing ? (
                        <textarea name="description" rows={3} value={form.description} onChange={handleChange} placeholder="Descripción opcional…" />
                      ) : <div className="v">{form.description || '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Ubicación{editing && <span className="sf2-hint" style={{ margin: 0, fontWeight: 400 }}>editable</span>}</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Ubicación / Centro</label>
                      {editing ? (
                        <LocationTreeSelect
                          locations={locations}
                          value={form.location_id}
                          onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                          placeholder="— sin ubicación —"
                        />
                      ) : <div className="v">{rutaUbicacionForm || selectedLocation?.name || '—'}</div>}
                    </div>
                    <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                      <label>Rack</label>
                      {editing ? (
                        <select name="rack_id" value={form.rack_id} onChange={handleChange}>
                          <option value="">— sin rack asignado —</option>
                          {filteredRacks.map(r => (
                            <option key={r.id} value={r.id}>{r.name || r.code}</option>
                          ))}
                        </select>
                      ) : <div className="v">{selectedRack?.name || selectedRack?.code || '—'}</div>}
                    </div>
                    <div className="sf2-field">
                      <label>Tamaño U</label>
                      {editing
                        ? <input type="number" min="1" max="10" name="size_u" value={form.size_u} onChange={handleChange} />
                        : <div className="v">{form.size_u ?? '—'}</div>}
                    </div>
                  </div>
                </div>

                <div className="sf2-card">
                  <h3>Puertos y red</h3>
                  <div className="sf2-fields" style={{ '--n': 2 }}>
                    <div className="sf2-field">
                      <label>{tabPuertosLabel}</label>
                      <div className="v">{form.port_count}</div>
                    </div>
                    <div className="sf2-field">
                      <label>Asignaciones IP</label>
                      <div className="v">{ipCount}</div>
                    </div>
                  </div>
                  <span className="sf2-link" role="button" tabIndex={0}
                    onClick={() => setActiveTab(tabPuertosKey)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setActiveTab(tabPuertosKey); } }}
                  >Abrir {tabPuertosLabel.toLowerCase()} →</span>
                  <span className="sf2-link" role="button" tabIndex={0} style={{ marginLeft: 14 }}
                    onClick={() => setActiveTab('red')}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setActiveTab('red'); } }}
                  >Ver red e IPAM →</span>
                  {elementType === 'switch' && (
                    <span className="sf2-link" role="button" tabIndex={0} style={{ marginLeft: 14 }}
                      onClick={() => setActiveTab('power')}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setActiveTab('power'); } }}
                    >Alimentación →</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Identificación ── */}
          {activeTab === 'identificacion' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Datos del elemento</div>
                <div className="row g-3">
                  <div className="col-md-5">
                    <label className="form-label">Código</label>
                    <input
                      name="code"
                      className="form-control"
                      placeholder={`Ej: ${elementType === 'panel' ? 'PP-SALA-A-01' : 'SW-CORE-01'}`}
                      value={form.code}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Estado</label>
                    <select name="status" className="form-select" value={form.status} onChange={handleChange}>
                      <option value="active">Activo</option>
                      <option value="maintenance">Mantenimiento</option>
                      <option value="offline">Fuera de servicio</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Conexiones (diagrama desde el punto de vista del elemento) ── */}
          {activeTab === 'conexiones' && (
            <div className={paneClass}>
              {isEdit && item?.id ? (
                <ConnectionDiagram
                  kind={elementType}
                  id={item.id}
                  companyId={companyId}
                  title={`Conexiones de ${item.name || lbl.singular}`}
                />
              ) : (
                <div className="dv-section">
                  <div className="dv-section-title">Conexiones</div>
                  <p className="dv-hint">Guarda el elemento para ver su diagrama de conexiones.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Puertos ── */}
          {activeTab === 'puertos' && (
            <div className={paneClass}>
              {isEdit ? (
                /* Existing element → inline port grid with editor */
                <SwitchPortsTab
                  element={item}
                  companyId={companyId}
                  poeCapable={!!form.poe_capable}
                  onPortsChanged={(nuevoTotal) => setForm(f => ({ ...f, port_count: nuevoTotal }))}
                />
              ) : (
                /* New element → port count presets */
                <div className="dv-section">
                  <div className="dv-section-title">{lbl.portLabel}</div>
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                    {PORT_PRESETS.map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => handlePortPreset(n)}
                        className={`btn ${form.port_count === n && !customPorts ? 'btn-primary' : 'btn-outline-secondary'}`}
                        style={{ minWidth: 56 }}
                      >
                        {n}
                      </button>
                    ))}
                    <input
                      type="number"
                      min="1"
                      max="999"
                      className="form-control"
                      placeholder="Personalizado..."
                      value={customPorts}
                      onChange={handleCustomPorts}
                      style={{ width: 150 }}
                    />
                  </div>
                  <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {lbl.portHint}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── Tab: Ubicación ── */}
          {activeTab === 'ubicacion' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Ubicación física</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Ubicación / Centro</label>
                    <LocationTreeSelect
                      locations={locations}
                      value={form.location_id}
                      onChange={(id) => handleChange({ target: { name: 'location_id', value: id } })}
                      placeholder="— sin filtrar —"
                    />
                  </div>
                  <div className="col-md-5">
                    <label className="form-label">Rack</label>
                    <select name="rack_id" className="form-select" value={form.rack_id} onChange={handleChange}>
                      <option value="">— sin rack asignado —</option>
                      {filteredRacks.map(r => (
                        <option key={r.id} value={r.id}>{r.name || r.code}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Tamaño U</label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      name="size_u"
                      className="form-control"
                      value={form.size_u}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Hardware ── */}
          {activeTab === 'hardware' && (
            <div className={paneClass}>
              <div className="dv-section">
                <div className="dv-section-title">Hardware</div>
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Fabricante</label>
                    {elementType === 'switch' ? (
                      <>
                        <div className="input-group">
                          <select
                            name="manufacturer"
                            className="form-select"
                            value={form.manufacturer}
                            onChange={e => { setField('manufacturer', e.target.value); setField('model', ''); setAddingManufacturer(false); setNewManufacturer(''); }}
                          >
                            <option value="">— Seleccionar —</option>
                            {manufacturerOptions.map(opt => (
                              <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setAddingManufacturer(v => !v)}
                            title="Añadir al maestro"
                          >
                            Añadir
                          </button>
                        </div>
                        {addingManufacturer && (
                          <div className="input-group mt-2">
                            <input
                              className="form-control"
                              placeholder="Nuevo fabricante"
                              value={newManufacturer}
                              onChange={e => setNewManufacturer(e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={async () => {
                                if (!newManufacturer.trim()) return;
                                const name = newManufacturer.trim();
                                setField('manufacturer', name);
                                await saveManufacturerToCatalog(name);
                                setNewManufacturer('');
                                setAddingManufacturer(false);
                              }}
                            >
                              Guardar
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingManufacturer(false); setNewManufacturer(''); }}>
                              Cancelar
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <input
                        name="manufacturer"
                        className="form-control"
                        placeholder="Cisco, Panduit, Patch-Rite..."
                        value={form.manufacturer}
                        onChange={handleChange}
                      />
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Modelo</label>
                    {elementType === 'switch' ? (
                      <>
                        <div className="input-group">
                          <select
                            name="model"
                            className="form-select"
                            value={form.model}
                            onChange={e => { handleChange(e); applyModelTemplate(e.target.value); setAddingModel(false); setNewModel(''); }}
                            disabled={!form.manufacturer && filteredModelOptions.length === 0}
                          >
                            <option value="">— Seleccionar —</option>
                            {filteredModelOptions.map(opt => (
                              <option key={opt.id || opt.name} value={opt.name}>{opt.name}</option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => setAddingModel(v => !v)}
                            title="Añadir al maestro"
                          >
                            Añadir
                          </button>
                        </div>
                        {addingModel && (
                          <div className="input-group mt-2">
                            <input
                              className="form-control"
                              placeholder="Nuevo modelo"
                              value={newModel}
                              onChange={e => setNewModel(e.target.value)}
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={async () => {
                                if (!newModel.trim()) return;
                                const name = newModel.trim();
                                setField('model', name);
                                await saveModelToCatalog(name);
                                setNewModel('');
                                setAddingModel(false);
                              }}
                            >
                              Guardar
                            </button>
                            <button type="button" className="btn btn-outline-secondary" onClick={() => { setAddingModel(false); setNewModel(''); }}>
                              Cancelar
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <input
                        name="model"
                        className="form-control"
                        placeholder="Modelo"
                        value={form.model}
                        onChange={handleChange}
                      />
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Número de serie</label>
                    <input
                      name="serial_number"
                      className="form-control"
                      placeholder="S/N"
                      value={form.serial_number}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Descripción / Notas</label>
                    <textarea
                      name="description"
                      className="form-control"
                      rows={3}
                      placeholder="Descripción opcional..."
                      value={form.description}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Tab: Red / IP ── */}
          {activeTab === 'red' && (
            <div className={paneClass}>
              <div className="dv-section">
                <ElementIpPanel
                  objectType="rack_element"
                  objectId={item?.id}
                  companyId={companyId}
                  elementName={item?.name || form.name}
                  onCountChange={setIpCount}
                />
                {elementType !== 'pdu' && (
                  <NetworkConnectionsPanel
                    objectType="rack_element"
                    objectId={item?.id}
                    companyId={companyId}
                    elementName={item?.name || form.name}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Tab: Accesos (switch/pdu) ── */}
          {activeTab === 'access' && elementType !== 'panel' && (
            <div className={paneClass}>
              <div className="dv-section">
                <DeviceAccessPanel
                  objectType="rack_element"
                  objectId={item?.id}
                  companyId={companyId}
                  elementName={item?.name || form.name}
                  onCountChange={setAccessCount}
                  canEdit={editing}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Alimentación (switch/pdu) ── */}
          {activeTab === 'power' && (
            <div className={paneClass}>
              <div className="dv-section">
                {/* PoE del switch */}
                {elementType === 'switch' && (
                  <div style={{ marginBottom: 16 }}>
                    <h3 className="dv-section-title">PoE (Power over Ethernet)</h3>
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="form-check mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="poe-capable"
                          checked={!!form.poe_capable}
                          onChange={e => setField('poe_capable', e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="poe-capable">
                          Este switch puede alimentar dispositivos por PoE
                        </label>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <label className="dv-label mb-0" htmlFor="poe-budget">Presupuesto PoE</label>
                        <input
                          id="poe-budget"
                          className="dv-input"
                          style={{ width: 110 }}
                          type="number"
                          min="0"
                          placeholder="600"
                          value={form.poe_budget_w}
                          disabled={!form.poe_capable}
                          onChange={e => setField('poe_budget_w', e.target.value)}
                        />
                        <span className="text-muted small">W</span>
                      </div>
                    </div>
                    <small className="text-muted d-block mt-1">
                      El presupuesto es la potencia PoE total de la fuente (p. ej. 370 W en
                      802.3at o 600-1440 W en 802.3bt). El PoE de cada puerto se marca en la
                      pestaña de Puertos.
                    </small>
                  </div>
                )}

                {/* PSU component manager — switches have integrated PSUs */}
                {elementType === 'switch' && isEdit && (
                  <SwitchPsuSection
                    elementId={item?.id}
                    companyId={companyId}
                  />
                )}
                <h3 className="dv-section-title">Conexiones PDU / Alimentación eléctrica</h3>
                <PowerConnectionsPanel
                  consumerType="rack_element"
                  consumerId={item?.id}
                  companyId={companyId}
                  rackId={item?.rack_id || (form.rack_id ? Number(form.rack_id) : null)}
                  elementName={item?.name || form.name}
                />
              </div>
            </div>
          )}

          {/* ── Tab: Enchufes PDU ── */}
          {activeTab === 'enchufes' && (
            <div className={paneClass}>
              <div className="dv-section">
                {!isEdit ? (
                  <>
                    <div className="dv-section-title">{lbl.portLabel}</div>
                    <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                      {PORT_PRESETS.map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => handlePortPreset(n)}
                          className={`btn ${form.port_count === n && !customPorts ? 'btn-primary' : 'btn-outline-secondary'}`}
                          style={{ minWidth: 56 }}
                        >
                          {n}
                        </button>
                      ))}
                      <input
                        type="number"
                        min="1"
                        max="999"
                        className="form-control"
                        placeholder="Personalizado..."
                        value={customPorts}
                        onChange={handleCustomPorts}
                        style={{ width: 150 }}
                      />
                    </div>
                    <p className="text-muted" style={{ fontSize: '0.85rem' }}>{lbl.portHint}</p>
                  </>
                ) : (
                  <PDUOutletMap pduId={item?.id} companyId={companyId} />
                )}
              </div>
            </div>
          )}
          </fieldset>

          <FormDirtyDock
            show={isCards && (isDirty || nameDirty)}
            saving={saving}
            onDiscard={handleDiscard}
            onSave={() => handleSubmit(null, { force: true })}
          />
          </FormCardsShell>
          </fieldset>
        </div>

        {/* ── Right sidebar ── */}
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType={elementType}
          ciId={item?.id}
          tipoLegible="el elemento"
        />
      </div>

      {/* Diálogo: eliminar (se lleva sus puertos/enchufes y las conexiones) */}
      {showDeleteConfirm && (
        <Dialog
          title={`Eliminar ${lbl.singular}`}
          onClose={() => setShowDeleteConfirm(false)}
          busy={saving}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={saving}>
                Cancelar
              </button>
              <button type="button" className="list-btn-danger" onClick={handleDelete} disabled={saving}>
                Eliminar
              </button>
            </>
          )}
        >
          <p>
            ¿Seguro que quieres eliminar <strong>{form.name || item?.name}</strong>? Se eliminarán también
            sus {elementType === 'pdu' ? 'enchufes y las conexiones de alimentación' : 'puertos y las conexiones'}{' '}
            que cuelgan de ellos. No se puede deshacer.
          </p>
        </Dialog>
      )}
    </div>
  );
}

export default PortElementForm;
